import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { audiencesForRole, roleBaseline, roleCellState, nextTweak } from "@/lib/access";
import { PermCell, PermFilters, PermLegend, PermTable, usePermFilter } from "./PermTable";
import TidyGrants from "./TidyGrants";
import { usePermissionPreview } from "./PermissionPreview";

/** Roles × permissions, showing the effective result of audience defaults +
 *  the role's own grants/revokes. Click cycles inherit → revoke, none → grant. */
export default function RoleMatrix({ catalog, roles, audiences, canManage, onRoleUpdated, onRolesChanged }) {
  const filter = usePermFilter(catalog);
  const [showSuper, setShowSuper] = useState(false);
  const [busy, setBusy] = useState("");
  const preview = usePermissionPreview();

  const cols = roles
    .filter((r) => showSuper || r.role_id !== "super_admin")
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    .map((r) => ({
      id: r.role_id, label: r.name, sub: r.base_role,
      title: `${r.name} · base ${r.base_role} · defaults from: ${audiencesForRole(r, audiences).map((a) => a.label).join(", ") || "no audience"}`,
    }));

  const click = async (role, perm) => {
    if (!canManage || role.role_id === "super_admin") return;
    const state = roleCellState(role, perm, audiences);
    const tweak = nextTweak(state);
    const key = `${role.role_id}|${perm}`;
    setBusy(key);
    try {
      // Clearing: pull from whichever list currently holds it.
      const body = tweak.granted ? { perm, grant: true, mode: "grant" }
        : tweak.revoked ? { perm, grant: true, mode: "revoke" }
        : { perm, grant: false, mode: state === "revoked" ? "revoke" : "grant" };
      if (!(await preview.confirm(`/roles/${role.role_id}/preview`, body, `Change ${perm} on ${role.name}?`))) return;
      const { data } = await api.post(`/roles/${role.role_id}/permissions/toggle`, body);
      onRoleUpdated?.(data);
      const verb = tweak.granted ? "Granted" : tweak.revoked ? "Revoked" : "Cleared";
      toast.success(`${verb} ${perm} on ${role.name}`, { id: key });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { id: key });
    } finally { setBusy(""); }
  };

  return (
    <div data-testid="role-matrix">
      <PermFilters filter={filter} testid="matrix-filter">
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <Checkbox checked={showSuper} onCheckedChange={() => setShowSuper((v) => !v)} data-testid="matrix-show-super" /> Show Super Admin
        </label>
        <TidyGrants canManage={canManage} onTidied={onRolesChanged} />
      </PermFilters>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <PermLegend />
        {canManage && <p className="text-[11px] text-slate-400">Click a cell: inherited → revoke · not granted → grant · click again to clear. Partner roles inherit from the audience matching each person&apos;s partner type.</p>}
      </div>
      <Card className="border-slate-200 shadow-sm">
        <PermTable groups={filter.shown} columns={cols} testid="permission-matrix"
          renderCell={(perm, col) => {
            const role = roles.find((r) => r.role_id === col.id);
            const state = roleCellState(role, perm, audiences);
            const from = roleBaseline(role, audiences).get(perm);
            const title = state === "inherited" && from ? `From audience defaults: ${from.join(", ")}` : undefined;
            return (
              <PermCell state={state} locked={col.id === "super_admin"} title={title}
                disabled={!canManage || busy === `${col.id}|${perm}`}
                onClick={() => click(role, perm)} testid={`matrix-${col.id}-${perm}`} />
            );
          }} />
      </Card>
      {preview.dialog}
    </div>
  );
}
